// src/components/common/PublicHeader.js — 공개 사이트 공통 헤더
// 두 방문자를 함께 안내: 행사 운영자(기능·요금·데모·블로그·무료로 시작하기) / 티켓 구매자(예약 조회·로그인)
import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { userService } from '../../services/userService';
import UserDropdown from './UserDropdown';
import '../../styles/public.scss';
import './PublicHeader.scss';

// 블로그는 서버 렌더링 페이지(/blog) — SPA 라우트가 아니므로 <a> 로 이동
const PRIMARY_NAV = [
  { label: '기능', to: { pathname: '/', hash: '#features' } },
  { label: '요금', to: { pathname: '/', hash: '#pricing' } },
  { label: '데모', to: '/demo' },
  { label: '블로그', href: '/blog' },
  { label: '자주 묻는 질문', to: '/faq' },
];

const PublicHeader = ({ title }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const isLoggedIn = userService.isAuthenticated();
  const user = userService.getUser();

  // 아래로 스크롤하면 헤더를 숨기고, 위로 올리면 다시 보인다 (맨 위 근처에서는 항상 보임)
  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY);
      setScrolled(y > 8);
      const dy = y - lastY;
      if (y < 96) {
        setHidden(false);
        lastY = y;
      } else if (Math.abs(dy) > 8) {
        setHidden(dy > 0);
        lastY = y;
      }
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // 메뉴 열림: 스크롤 잠금 + ESC 닫기
  useEffect(() => {
    if (!menuOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

  const logout = useCallback(() => {
    userService.logout();
    window.location.href = '/';
  }, []);

  const renderNavItem = (item, className) =>
    item.href ? (
      <a key={item.label} href={item.href} className={className}>{item.label}</a>
    ) : typeof item.to === 'string' ? (
      <NavLink key={item.label} to={item.to} className={({ isActive }) => `${className}${isActive ? ' is-active' : ''}`}>
        {item.label}
      </NavLink>
    ) : (
      <Link key={item.label} to={item.to} className={className}>{item.label}</Link>
    );

  return (
    <header
      className={`public-header${scrolled ? ' public-header--scrolled' : ''}${hidden && !menuOpen ? ' public-header--hidden' : ''}`}
      onFocus={() => setHidden(false)}
    >
      <a className="gt-skip-link" href="#main">본문 바로가기</a>
      <div className="public-header__bar gt-container">
        <Link to="/" className="public-header__logo" aria-label="GATEON 홈">GATEON</Link>

        {title && <span className="public-header__page-title" aria-hidden="true">{title}</span>}

        <nav className="public-header__nav" aria-label="주요 메뉴">
          {PRIMARY_NAV.map((item) => renderNavItem(item, 'public-header__nav-link'))}
        </nav>

        <div className="public-header__utility">
          <NavLink to="/lookup" className={({ isActive }) => `public-header__util-link${isActive ? ' is-active' : ''}`}>
            예약 조회
          </NavLink>
          {isLoggedIn ? (
            <UserDropdown user={user} />
          ) : (
            <Link to="/login" className="public-header__util-link">로그인</Link>
          )}
          <Link to="/business/signup" className="gt-btn gt-btn--dark gt-btn--sm">무료로 시작하기</Link>
        </div>

        <button
          type="button"
          className="public-header__menu-btn"
          aria-expanded={menuOpen}
          aria-controls="public-mobile-menu"
          aria-label={menuOpen ? '메뉴 닫기' : '메뉴 열기'}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
        </button>
      </div>

      <div
        id="public-mobile-menu"
        className={`public-header__sheet${menuOpen ? ' is-open' : ''}`}
        hidden={!menuOpen}
      >
        <div className="public-header__sheet-inner gt-container">
          <section className="public-header__group" aria-labelledby="mm-organizer">
            <h2 id="mm-organizer">행사를 운영하시나요</h2>
            {PRIMARY_NAV.slice(0, 4).map((item) => renderNavItem(item, 'public-header__sheet-link'))}
            <Link to="/business/signup" className="gt-btn gt-btn--primary public-header__sheet-cta">무료로 시작하기</Link>
            <Link to="/admin/login" className="public-header__sheet-sub">운영자 로그인</Link>
          </section>

          <section className="public-header__group" aria-labelledby="mm-buyer">
            <h2 id="mm-buyer">티켓을 예매하셨나요</h2>
            <Link to="/lookup" className="public-header__sheet-link">예약 조회</Link>
            {isLoggedIn ? (
              <>
                <Link to="/mypage" className="public-header__sheet-link">마이페이지</Link>
                <button type="button" className="public-header__sheet-link public-header__sheet-logout" onClick={logout}>
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="public-header__sheet-link">로그인</Link>
                <Link to="/register" className="public-header__sheet-sub">회원가입</Link>
              </>
            )}
          </section>

          <section className="public-header__group" aria-labelledby="mm-help">
            <h2 id="mm-help">도움이 필요하신가요</h2>
            <Link to="/faq" className="public-header__sheet-link">자주 묻는 질문</Link>
            <Link to="/notices" className="public-header__sheet-link">공지사항</Link>
          </section>
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
